import { useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { CATEGORIES, WORKS } from "../lib/media";

export default function Portfolio() {
    const [filter, setFilter] = useState("all");

    const items = useMemo(
        () => (filter === "all" ? WORKS : WORKS.filter((w) => w.category === filter)),
        [filter]
    );

    return (
        <section
            id="work"
            className="relative bg-ink-950 py-20 md:py-36 px-5 md:px-10"
            data-testid="portfolio-section"
        >
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-8 mb-14">
                <div>
                    <div className="overline mb-6" data-testid="portfolio-overline">
                        <span className="text-flash">■</span>&nbsp;&nbsp;01 — Selected Work
                    </div>
                    <h2
                        className="font-display uppercase text-bone-50 leading-[0.85] tracking-[-0.03em]"
                        style={{ fontSize: "clamp(2.5rem, 8vw, 8rem)" }}
                        data-testid="portfolio-heading"
                    >
                        Portfolio<span className="text-flash">.</span>
                    </h2>
                </div>
                <p className="max-w-sm text-bone-400 font-light text-base md:text-lg">
                    Una <span className="font-serif-i text-bone-50">selezione</span> dei lavori più recenti.
                    Filtra per categoria per esplorare storie diverse.
                </p>
            </div>

            {/* Filters */}
            <div
                className="flex flex-wrap gap-x-6 gap-y-3 border-t border-white/10 pt-6 mb-10 md:mb-14"
                data-testid="portfolio-filters"
            >
                {CATEGORIES.map((c) => {
                    const active = filter === c.id;
                    return (
                        <button
                            key={c.id}
                            onClick={() => setFilter(c.id)}
                            className={`font-mono text-[11px] tracking-[0.25em] uppercase transition-colors duration-300 ${active ? "text-flash" : "text-bone-400 hover:text-bone-50"}`}
                            data-testid={`portfolio-filter-${c.id}`}
                        >
                            {c.label}
                            {active && <span className="ml-2 text-flash">●</span>}
                        </button>
                    );
                })}
            </div>

            {/* Grid */}
            <motion.div
                layout
                className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 sm:gap-2.5 md:gap-3"
                data-testid="portfolio-grid"
            >
                <AnimatePresence mode="popLayout">
                    {items.map((w, i) => {
                        const span = "aspect-[3/4]";
                        return (
                            <motion.figure
                                key={`${w.url}-${filter}`}
                                layout
                                initial={{ opacity: 0, y: 40 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: 20 }}
                                viewport={{ once: true, margin: "-10%" }}
                                transition={{ duration: 0.9, ease: [0.19, 1, 0.22, 1], delay: (i % 3) * 0.06 }}
                                className={`pf-tile ${span} relative group`}
                                data-cursor="view"
                                data-testid={`portfolio-item-${i}`}
                            >
                                <img
                                    src={w.url}
                                    alt={`${w.title} — ${CATEGORIES.find((c) => c.id === w.category)?.label} — Francesco Ciotoli fotografo Ceccano Frosinone${w.meta ? ` (${w.meta})` : ""}`}
                                    title={`${w.title} · ${w.meta}`}
                                    className={`absolute inset-0 w-full h-full ${w.fit === "contain" ? "object-contain bg-ink-900 p-2" : "object-cover"}`}
                                    style={{ objectPosition: w.pos || "center 25%" }}
                                    loading="lazy"
                                />
                                <figcaption className="absolute left-3 right-3 bottom-3 md:left-6 md:right-6 md:bottom-6 flex items-end justify-between text-bone-50 z-10 opacity-100 md:opacity-0 md:group-hover:opacity-100 transition-opacity duration-500">
                                    <div>
                                        <div className="overline text-bone-50/80 mb-1 text-[9px] md:text-[11px]">
                                            {CATEGORIES.find((c) => c.id === w.category)?.label}
                                        </div>
                                        <div className="font-display uppercase text-xl md:text-3xl leading-none tracking-tight">
                                            {w.title}<span className="text-flash">.</span>
                                        </div>
                                    </div>
                                    <div className="hidden md:block font-mono text-[10px] tracking-[0.2em] uppercase text-bone-200">
                                        {w.meta}
                                    </div>
                                </figcaption>
                                <div className="absolute inset-0 bg-gradient-to-t from-ink-950/70 via-transparent to-transparent opacity-100 group-hover:opacity-100 pointer-events-none" />
                            </motion.figure>
                        );
                    })}
                </AnimatePresence>
            </motion.div>
        </section>
    );
}
